/**
 * m3 echo Frontend - WebSocket Store
 *
 * Centralized WebSocket management using Zustand.
 * Handles job progress subscriptions and state management.
 */

import { create } from 'zustand';
import websocketService, { JobProgressMessage } from '../services/websocket';

interface WebSocketState {
  // Job progress tracking
  jobProgress: Record<number, JobProgressMessage>;
  subscribedJobs: Set<number>;

  // Actions
  subscribeToJob: (jobId: number, onUpdate?: () => void) => void;
  unsubscribeFromJob: (jobId: number) => void;
  updateProgress: (jobId: number, message: JobProgressMessage) => void;
  clearProgress: (jobId: number) => void;
  getProgress: (jobId: number) => JobProgressMessage | undefined;
}

export const useWebSocketStore = create<WebSocketState>((set, get) => ({
  jobProgress: {},
  subscribedJobs: new Set(),

  /**
   * Subscribe to job progress updates
   * @param jobId - The job ID to subscribe to
   * @param onUpdate - Optional callback when job status updates
   */
  subscribeToJob: (jobId: number, onUpdate?: () => void) => {
    const { subscribedJobs } = get();

    // Don't subscribe if already subscribed
    if (subscribedJobs.has(jobId)) {
      console.log(`[WebSocketStore] Already subscribed to job ${jobId}`);
      return;
    }

    console.log(`[WebSocketStore] Subscribing to job ${jobId}...`);

    // Create handler for this job
    const handler = (message: JobProgressMessage) => {
      console.log(`[WebSocketStore] Job ${jobId} progress:`, message);

      // Update progress state
      set(state => ({
        jobProgress: {
          ...state.jobProgress,
          [jobId]: message
        }
      }));

      // Call update callback if provided (for any progress update)
      if (onUpdate) {
        onUpdate();
      }

      // Handle job completion
      if (message.type === 'job_completed') {
        console.log(`[WebSocketStore] Job ${jobId} completed`);

        // Auto-unsubscribe after a delay to show final message
        setTimeout(() => {
          get().unsubscribeFromJob(jobId);
        }, 2000);
      }
    };

    // Subscribe via WebSocket service
    websocketService.subscribeToJob(jobId, handler);

    // Add to subscribed jobs set
    set(state => ({
      subscribedJobs: new Set([...state.subscribedJobs, jobId])
    }));
  },

  /**
   * Unsubscribe from job progress updates
   * @param jobId - The job ID to unsubscribe from
   */
  unsubscribeFromJob: (jobId: number) => {
    const { subscribedJobs } = get();

    if (!subscribedJobs.has(jobId)) {
      return;
    }

    console.log(`[WebSocketStore] Unsubscribing from job ${jobId}`);

    // Unsubscribe via WebSocket service
    websocketService.unsubscribeFromJob(jobId);

    // Remove from subscribed jobs and clear progress
    set(state => {
      const newSubscribed = new Set(state.subscribedJobs);
      newSubscribed.delete(jobId);

      const { [jobId]: _, ...newProgress } = state.jobProgress;

      return {
        subscribedJobs: newSubscribed,
        jobProgress: newProgress
      };
    });
  },

  /**
   * Manually update progress for a job
   * @param jobId - The job ID
   * @param message - The progress message
   */
  updateProgress: (jobId: number, message: JobProgressMessage) => {
    set(state => ({
      jobProgress: {
        ...state.jobProgress,
        [jobId]: message
      }
    }));
  },

  /**
   * Clear progress for a job
   * @param jobId - The job ID
   */
  clearProgress: (jobId: number) => {
    set(state => {
      const { [jobId]: _, ...newProgress } = state.jobProgress;
      return { jobProgress: newProgress };
    });
  },

  /**
   * Get progress for a specific job
   * @param jobId - The job ID
   * @returns The progress message or undefined
   */
  getProgress: (jobId: number) => {
    return get().jobProgress[jobId];
  }
}));
